import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  useClientIdFormats,
  useEmployeeIdConfig,
  useEmployeeIdPreview,
  useRemoveClientIdFormat,
  useSaveClientIdFormat,
  useSaveEmployeeIdConfig,
} from "../../api/queries/adminToolsQueries";
import { useCompanies } from "../../api/queries/organizationQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import usePermissions from "../../hooks/usePermissions";
import { getApiErrorMessage } from "../../utils/apiError";
import { currentPeriod } from "../../utils/dateTime";
import "./admin.css";

const EMPTY = [];
const DEFAULTS = {
  prefix: "",
  includeYear: true,
  numberLength: 5,
  separator: "-",
  suffix: "",
  resetEveryYear: false,
  format: "",
  nextNumber: "",
  isActive: true,
};
export const ID_SOURCE = {
  CLIENT: "client format",
  COMPANY: "company format",
  GLOBAL: "global default",
  BUILT_IN: "built-in default",
};

/**
 * Local preview while typing (the server preview is authoritative).
 * With a custom format the tokens {prefix} {year} {number} {suffix} are
 * replaced (the year always shows when the format has {year}, like the
 * server); otherwise prefix, year, number and suffix are joined by the separator.
 */
export const previewId = (cfg, number = 1) => {
  const len = Math.min(Math.max(Number(cfg.numberLength) || 1, 1), 12);
  const num = String(Number(cfg.nextNumber) || number).padStart(len, "0");
  const year = String(currentPeriod().year);
  if (cfg.format && cfg.format.trim()) {
    return cfg.format
      .replace(/\{prefix\}/g, cfg.prefix || "")
      .replace(/\{year\}/g, year)
      .replace(/\{number\}/g, num)
      .replace(/\{suffix\}/g, cfg.suffix || "");
  }
  return [cfg.prefix, cfg.includeYear ? year : "", num, cfg.suffix]
    .filter(Boolean)
    .join(cfg.separator ?? "");
};

/** One-line summary of a saved config (for the clients table). */
export const formatSummary = (c) =>
  c
    ? c.format?.trim() ||
      [c.prefix, c.includeYear ? "{year}" : "", "{number}", c.suffix]
        .filter(Boolean)
        .join(c.separator ?? "")
    : "";

const fromConfig = (c, fallbackPrefix = "") => ({
  prefix: c?.prefix ?? fallbackPrefix,
  includeYear: c?.includeYear ?? true,
  numberLength: c?.numberLength ?? 5,
  separator: c?.separator ?? "-",
  suffix: c?.suffix ?? "",
  resetEveryYear: !!c?.resetEveryYear,
  format: c?.format ?? "",
  nextNumber: "",
  isActive: c?.isActive ?? true,
});

const problemsOf = (form) => {
  const p = [];
  if (!(Number(form.numberLength) >= 1 && Number(form.numberLength) <= 12))
    p.push("Number length must be 1–12.");
  if (form.format.trim() && !form.format.includes("{number}"))
    p.push("The format must contain {number}.");
  if (form.nextNumber !== "" && !(Number(form.nextNumber) >= 1))
    p.push("Next number must be 1 or more.");
  return p;
};
const toBody = (form, withActive) => ({
  prefix: form.prefix,
  includeYear: form.includeYear,
  numberLength: Number(form.numberLength),
  separator: form.separator,
  suffix: form.suffix,
  resetEveryYear: form.resetEveryYear,
  format: form.format.trim() || undefined,
  ...(form.nextNumber !== "" && { nextNumber: Number(form.nextNumber) }),
  ...(withActive && { isActive: form.isActive }),
});
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};

/** The format fields (shared by the company form and the client dialog). */
const FormatFields = ({ form, set, idPrefix, withActive, disabled }) => (
  <fieldset className="row g-3" disabled={disabled}>
    {withActive && (
      <div className="col-12">
        <div className="form-check form-switch">
          <input
            id={`${idPrefix}-active`}
            type="checkbox"
            className="form-check-input"
            checked={form.isActive}
            onChange={(e) => set({ isActive: e.target.checked })}
          />
          <label
            htmlFor={`${idPrefix}-active`}
            className="form-check-label fs-085"
          >
            Active (off = this client's employees get the company format)
          </label>
        </div>
      </div>
    )}
    <div className="col-sm-4">
      <label htmlFor={`${idPrefix}-prefix`} className="profile-label">
        Prefix
      </label>
      <input
        id={`${idPrefix}-prefix`}
        className="form-control"
        value={form.prefix}
        onChange={(e) => set({ prefix: e.target.value })}
        placeholder="EMI"
        maxLength={20}
      />
    </div>
    <div className="col-sm-4">
      <label htmlFor={`${idPrefix}-sep`} className="profile-label">
        Separator
      </label>
      <input
        id={`${idPrefix}-sep`}
        className="form-control"
        value={form.separator}
        onChange={(e) => set({ separator: e.target.value })}
        maxLength={3}
        placeholder="-"
      />
    </div>
    <div className="col-sm-4">
      <label htmlFor={`${idPrefix}-suffix`} className="profile-label">
        Suffix
      </label>
      <input
        id={`${idPrefix}-suffix`}
        className="form-control"
        value={form.suffix}
        onChange={(e) => set({ suffix: e.target.value })}
        maxLength={20}
      />
    </div>
    <div className="col-sm-4">
      <label htmlFor={`${idPrefix}-len`} className="profile-label">
        Number length
      </label>
      <input
        id={`${idPrefix}-len`}
        type="number"
        min="1"
        max="12"
        className="form-control"
        value={form.numberLength}
        onChange={(e) => set({ numberLength: e.target.value })}
      />
    </div>
    <div className="col-sm-8 d-flex flex-column justify-content-end gap-1">
      <div className="form-check form-switch">
        <input
          id={`${idPrefix}-year`}
          type="checkbox"
          className="form-check-input"
          checked={form.includeYear}
          onChange={(e) => set({ includeYear: e.target.checked })}
        />
        <label htmlFor={`${idPrefix}-year`} className="form-check-label fs-085">
          Include the year
        </label>
      </div>
      <div className="form-check form-switch">
        <input
          id={`${idPrefix}-reset`}
          type="checkbox"
          className="form-check-input"
          checked={form.resetEveryYear}
          onChange={(e) => set({ resetEveryYear: e.target.checked })}
        />
        <label
          htmlFor={`${idPrefix}-reset`}
          className="form-check-label fs-085"
        >
          Restart numbering every year
        </label>
      </div>
    </div>
    <div className="col-12">
      <div className="fs-070 text-slate-500">
        <i className="bi bi-info-circle me-1"></i>For now "Include the year" has
        no effect on a custom format: the year shows whenever the format
        contains {"{year}"}.
      </div>
    </div>
    <div className="col-12">
      <label htmlFor={`${idPrefix}-format`} className="profile-label">
        Custom format{" "}
        <span className="text-slate-400 fw-normal">(optional)</span>
      </label>
      <input
        id={`${idPrefix}-format`}
        className="form-control font-monospace"
        value={form.format}
        onChange={(e) => set({ format: e.target.value })}
        placeholder="{prefix}-{year}{number}"
      />
      <div className="fs-070 text-slate-500 mt-1">
        Tokens: {"{prefix} {year} {number} {suffix}"}. Must contain {"{number}"}
        . Leave empty to join the parts with the separator.
      </div>
    </div>
    <div className="col-sm-6">
      <label htmlFor={`${idPrefix}-next`} className="profile-label">
        Set next number{" "}
        <span className="text-slate-400 fw-normal">(optional)</span>
      </label>
      <input
        id={`${idPrefix}-next`}
        type="number"
        min="1"
        className="form-control"
        value={form.nextNumber}
        onChange={(e) => set({ nextNumber: e.target.value })}
        placeholder="keep counting"
      />
    </div>
    {form.nextNumber !== "" && (
      <div className="col-12">
        <div className="alert alert-warning fs-080 py-2 m-0">
          <i className="bi bi-exclamation-triangle me-1"></i>
          Changing the next number can create IDs that already exist if you set
          it lower than the last one used.
        </div>
      </div>
    )}
  </fieldset>
);

const ErrorList = ({ errors }) =>
  errors.length === 0 ? null : (
    <div className="alert alert-danger fs-085 py-2">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <ul className="mb-0 ps-3">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );

/** Company format: form + local and server preview. */
const CompanyForm = ({ companyId, config, canEdit }) => {
  const [form, setForm] = useState(() =>
    config ? fromConfig(config) : DEFAULTS,
  );
  const [errors, setErrors] = useState([]);
  const preview = useEmployeeIdPreview(companyId);
  const save = useSaveEmployeeIdConfig();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));

  const submit = (e) => {
    e.preventDefault();
    const problems = problemsOf(form);
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { companyId, ...toBody(form, false) },
      {
        onSuccess: () => {
          toast.success("Employee ID format saved.");
          set({ nextNumber: "" });
        },
        onError: (err) => setErrors(messagesOf(err, "Could not save.")),
      },
    );
  };

  return (
    <form className="row g-3" onSubmit={submit} noValidate>
      <div className="col-lg-7">
        <section className="admin-card">
          <h2 className="admin-card-title">
            <i className="bi bi-sliders"></i>Company format
          </h2>
          <ErrorList errors={errors} />
          <FormatFields
            form={form}
            set={set}
            idPrefix="id"
            disabled={!canEdit}
          />
          {canEdit && (
            <div className="d-flex justify-content-end mt-3">
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={save.isPending}
              >
                {save.isPending && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                Save
              </button>
            </div>
          )}
        </section>
      </div>
      <div className="col-lg-5">
        <section className="admin-card">
          <h2 className="admin-card-title">
            <i className="bi bi-eye"></i>Preview
          </h2>
          <div className="approval-meta-label">While typing</div>
          <div className="id-preview mb-3">{previewId(form) || "—"}</div>
          <div className="approval-meta-label">
            Next ID the system will give (saved settings)
          </div>
          <div className="fs-5 fw-bold font-monospace text-navy">
            {preview.isLoading
              ? "…"
              : preview.isError
                ? "—"
                : preview.data?.preview || "—"}
          </div>
          {preview.data?.source && (
            <div className="fs-075 text-slate-500">
              From the {ID_SOURCE[preview.data.source] || preview.data.source}.
            </div>
          )}
          {!config && (
            <p className="fs-075 text-slate-500 mt-2 mb-0">
              No format saved for this company yet: the global default is used.
            </p>
          )}
        </section>
      </div>
    </form>
  );
};

/** Edit one client's format (dialog). */
const ClientFormatDialog = ({ client, onClose }) => {
  const [form, setForm] = useState(() =>
    fromConfig(client.config, client.clientCode || ""),
  );
  const [errors, setErrors] = useState([]);
  const [savedPreview, setSavedPreview] = useState(client.preview || null);
  const save = useSaveClientIdFormat();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));

  const submit = () => {
    const problems = problemsOf(form);
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      { clientId: client.clientId, ...toBody(form, true) },
      {
        onSuccess: (res) => {
          setSavedPreview(res?.preview || null);
          set({ nextNumber: "" });
          toast.success(`ID format saved for ${client.clientName}.`);
        },
        onError: (err) => setErrors(messagesOf(err, "Could not save.")),
      },
    );
  };

  return (
    <Dialog
      title={`ID format: ${client.clientName}`}
      subtitle={
        client.clientCode
          ? `Client code ${client.clientCode} · one running number across companies`
          : undefined
      }
      size="lg"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      <ErrorList errors={errors} />
      <FormatFields form={form} set={set} idPrefix="cid" withActive />
      <div className="d-flex flex-wrap gap-4 mt-3">
        <div>
          <div className="approval-meta-label">While typing</div>
          <div className="id-preview">{previewId(form) || "—"}</div>
        </div>
        <div>
          <div className="approval-meta-label">Next ID (saved settings)</div>
          <div className="fs-5 fw-bold font-monospace text-navy">
            {savedPreview || "—"}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

/** Clients and their own ID formats (deployed employees). */
const ClientFormats = ({ canEdit }) => {
  const query = useClientIdFormats();
  const remove = useRemoveClientIdFormat();
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const rows = (query.data || EMPTY).filter(
    (c) =>
      !q ||
      `${c.clientCode || ""} ${c.clientName || ""}`.toLowerCase().includes(q),
  );

  const backToCompany = (c) => {
    if (
      !window.confirm(
        `${c.clientName}: go back to the company format? Existing IDs don't change.`,
      )
    )
      return;
    remove.mutate(c.clientId, {
      onSuccess: () =>
        toast.success(`${c.clientName} now uses the company format.`),
      onError: (err) =>
        toast.error(
          getApiErrorMessage(err, "Could not remove the client format."),
        ),
    });
  };

  return (
    <>
      <p className="fs-085 text-slate-600">
        New employees of a client with an <strong>active</strong> client format
        get their ID from it (one running number per client, across companies).
        Otherwise the company format is used. Existing IDs never change.
      </p>
      <div className="mb-3">
        <input
          type="search"
          className="filter-input"
          placeholder="Search client code or name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search clients"
        />
      </div>
      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(query.error, "Could not load clients.")}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0 admin-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Code</th>
                  <th className="py-3">Client</th>
                  <th className="py-3">Status</th>
                  <th className="py-3">Format</th>
                  <th className="py-3">Next ID</th>
                  <th className="py-3 pe-4 text-end">Actions</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {rows.map((c) => (
                  <tr key={c.clientId}>
                    <td className="ps-4 font-monospace">
                      {c.clientCode || "—"}
                    </td>
                    <td className="fw-semibold">{c.clientName}</td>
                    <td>
                      {c.status ? (
                        <span className="chip chip--sm">{c.status}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {c.config ? (
                        <>
                          <span className="font-monospace fs-080">
                            {formatSummary(c.config)}
                          </span>
                          {c.config.isActive === false && (
                            <span className="status-pill status-pill--gray ms-2">
                              Inactive
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-500">
                          Uses company format
                        </span>
                      )}
                    </td>
                    <td className="font-monospace">{c.preview || "—"}</td>
                    <td className="pe-4 text-end text-nowrap">
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn"
                            onClick={() => setEditing(c)}
                          >
                            <i className="bi bi-pencil"></i>{" "}
                            {c.config ? "Edit" : "Set format"}
                          </button>
                          {c.config && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light border att-action-btn ms-1"
                              onClick={() => backToCompany(c)}
                              disabled={remove.isPending}
                            >
                              Use company format
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {!query.isLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="approval-empty">
                      No clients found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
          </div>
        )}
      </div>
      {editing && (
        <ClientFormatDialog
          key={editing.clientId}
          client={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};

/** Admins/HR: the format of new employee IDs, per company and per client. */
const EmployeeIdFormatPage = () => {
  const user = useCurrentUser();
  const { companyWide } = usePermissions();
  const canEdit = companyWide("company-setup", "modify");
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "clients" ? "clients" : "company";
  const { data: companies = EMPTY } = useCompanies();
  const [companyId, setCompanyId] = useState(user.companyId || "");
  const config = useEmployeeIdConfig(companyId);
  const companyOptions = useMemo(
    () => companies.map((c) => ({ id: c.id, name: c.companyName || c.name })),
    [companies],
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-3">
          <Link
            to="/admin"
            className="text-slate-500 fs-080 text-decoration-none"
          >
            <i className="bi bi-arrow-left"></i> Administration
          </Link>
          <h1 className="fw-bold text-dark m-0 page-title">
            Employee ID Format
          </h1>
          <p className="text-muted small mb-0">
            How new employee IDs are numbered: client format, else company
            format, else the global default.
          </p>
        </div>
        <div className="map-tabs" role="tablist">
          {[
            ["company", "Company format"],
            ["clients", "Client formats"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={`map-tab ${tab === k ? "active" : ""}`}
              onClick={() =>
                setParams(k === "company" ? {} : { tab: k }, { replace: true })
              }
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "clients" ? (
          <ClientFormats canEdit={canEdit} />
        ) : (
          <>
            {companyOptions.length > 1 && (
              <div className="mb-3 d-flex align-items-center gap-2">
                <label htmlFor="id-company" className="fs-080 text-slate-600">
                  Company
                </label>
                <select
                  id="id-company"
                  className="status-select"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  {!companyId && <option value="">Choose a company…</option>}
                  {companyOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {config.isError && (
              <div className="alert alert-danger fs-085">
                {getApiErrorMessage(config.error, "Could not load the format.")}
              </div>
            )}
            {config.isLoading ? (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            ) : (
              !config.isError &&
              companyId && (
                <CompanyForm
                  key={companyId}
                  companyId={companyId}
                  config={config.data}
                  canEdit={canEdit}
                />
              )
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default EmployeeIdFormatPage;
